import React from 'react';
import cn from 'classnames';
import { CheckIcon, CloseIcon, ErrorIcon, InfoIcon } from '../../Icons/';

export type ToastVariant = 'info' | 'success' | 'error';

export interface ToastProps {
  title: string;
  message?: string;
  variant?: ToastVariant;
  showIcon?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  onClose?: () => void;
  className?: string;
}

export const Toast: React.FC<ToastProps> = ({
  title,
  message,
  variant = 'info',
  showIcon = true,
  actionLabel,
  onAction,
  onClose,
  className,
}) => {
  const variantStyles = {
    info: {
      iconBg: 'bg-ds-bg-feedback-info',
      iconText: 'text-ds-text-inverse-info',
      Icon: InfoIcon,
    },
    success: {
      iconBg: 'bg-ds-bg-surface-2',
      iconText: 'text-ds-text-inverse-info',
      Icon: CheckIcon,
    },
    error: {
      iconBg: 'bg-ds-bg-feedback-soft-error',
      iconText: 'text-ds-text-feedback-error',
      Icon: ErrorIcon,
    },
  } as const;

  const { iconBg, iconText, Icon } = variantStyles[variant];
  const hasAction = Boolean(actionLabel && onAction);
  const hasBottomRow = Boolean(message) || hasAction;

  return (
    <div
      className={cn(
        'flex px-ds-4 py-ds-2-5 w-ds-toast gap-ds-3',
        hasBottomRow ? 'items-start' : 'items-center',
        'rounded-ds-3 border-solid border-ds-regular border-ds-aqua-300',
        'bg-ds-survey-footer shadow-ds-elevation-brand-l',
        className,
      )}
    >
      {/* Icon */}
      {showIcon && (
        <div
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-full shrink-0',
            iconBg,
            iconText,
          )}
          aria-hidden
        >
          <Icon className="w-5 h-5 text-current" />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col flex-1 min-w-0 min-h-10">
        <div className="flex items-center min-w-0">
          <p className="text-ds-title-s font-light text-ds-text-inverse-1 flex-1 min-w-0 pr-2">
            {title}
          </p>

          {onClose && (
            <button
              onClick={onClose}
              className={cn(
                'flex items-center justify-center w-10 h-10 shrink-0',
                'rounded-full cursor-pointer',
                'transition-colors duration-200',
                'hover:bg-white/10 active:bg-white/20',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-aqua-300',
              )}
              aria-label="Close notification"
            >
              <div className="w-6 h-6 text-ds-text-inverse-3">
                <CloseIcon />
              </div>
            </button>
          )}
        </div>

        {hasBottomRow && (
          <div className="flex flex-col flex-1 min-w-0">
            {message && (
              <p className="text-ds-text-s font-normal text-ds-text-inverse-3 min-w-0 mt-1">
                {message}
              </p>
            )}

            {hasAction && (
              <div className="mt-auto flex justify-end">
                <button
                  onClick={onAction}
                  className={cn(
                    'flex items-center justify-center px-3 py-2.5',
                    'rounded-full text-center cursor-pointer',
                    'text-ds-text-2 text-ds-text-s font-semibold',
                    'transition-colors duration-200',
                    'hover:bg-white/10 active:bg-white/20',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-aqua-300',
                  )}
                >
                  {actionLabel}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Toast;
